/**
 * Header Component
 *
 * A responsive horizontal header component with support for logo, title, actions,
 * and user menu. Designed to work with AppShell for mobile menu integration.
 *
 * @fileoverview Header component for the multi-tenant UI
 *
 * @example
 * // Basic usage with title and logo
 * <Header
 *   logo={<Image src="/logo.png" alt="Logo" h="32px" />}
 *   title="Dashboard"
 * />
 *
 * @example
 * // Full featured header
 * <Header
 *   logo={<Logo />}
 *   title="My App"
 *   onMenuClick={toggleSidebar}
 *   actions={
 *     <>
 *       <ThemeToggle />
 *       <NotificationBell />
 *     </>
 *   }
 *   userMenu={<UserDropdown user={currentUser} />}
 * />
 */

import React, { type ReactNode } from 'react';
import {
  Box,
  Flex,
  HStack,
  Heading,
  IconButton,
  Spacer,
  useColorModeValue,
  type BoxProps,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Props for the Header component
 */
export interface HeaderProps extends Omit<BoxProps, 'children'> {
  /** Logo element (typically an Image or custom component) */
  logo?: ReactNode;
  /** Application or page title */
  title?: string;
  /** Callback when mobile menu button is clicked */
  onMenuClick?: () => void;
  /** Whether to show the mobile menu button (default: true on mobile) */
  showMenuButton?: boolean;
  /** Action buttons slot (theme toggle, notifications, etc.) */
  actions?: ReactNode;
  /** User menu slot (avatar dropdown, user info, etc.) */
  userMenu?: ReactNode;
  /** Organization switcher slot (for multi-tenant support) */
  orgSwitcher?: ReactNode;
  /** Custom height for the header (default: 64px) */
  height?: string | number;
  /** Whether to show a bottom border (default: true) */
  showBorder?: boolean;
}

// ============================================================================
// Component Implementation
// ============================================================================

/**
 * Header - Horizontal navigation header component
 *
 * Features:
 * - Logo and title display
 * - Mobile menu button integration with AppShell
 * - Actions slot for buttons/icons
 * - User menu slot for account dropdown
 * - Organization switcher slot for multi-tenant apps
 * - Full dark mode support
 * - Responsive design
 *
 * @param props - Component props (see HeaderProps interface)
 * @returns The rendered Header component
 */
export function Header({
  logo,
  title,
  onMenuClick,
  showMenuButton = true,
  actions,
  userMenu,
  orgSwitcher,
  height = '64px',
  showBorder = true,
  ...boxProps
}: HeaderProps): React.ReactElement {
  // -------------------------------------------------------------------------
  // Theming - Uses useColorModeValue for light/dark mode support
  // -------------------------------------------------------------------------

  // Background color
  const bg = useColorModeValue('white', 'xacto.darkSurface');

  // Border color
  const borderColor = useColorModeValue('gray.200', 'xacto.mutedNavy');

  // Text colors
  const titleColor = useColorModeValue('gray.800', 'xacto.pureWhite');

  // -------------------------------------------------------------------------
  // Main Render
  // -------------------------------------------------------------------------

  return (
    <Box
      as="header"
      bg={bg}
      h={height}
      borderBottom={showBorder ? '1px' : 'none'}
      borderColor={borderColor}
      px={{ base: 4, md: 6 }}
      {...boxProps}
    >
      <Flex
        h="full"
        align="center"
        justify="space-between"
      >
        {/* Left Section: Menu Button + Logo + Title */}
        <HStack spacing={{ base: 2, md: 4 }}>
          {/* Mobile Menu Button */}
          {showMenuButton && onMenuClick && (
            <IconButton
              aria-label="Open menu"
              icon={<HamburgerIcon />}
              variant="ghost"
              display={{ base: 'flex', md: 'none' }}
              onClick={onMenuClick}
              size="md"
            />
          )}

          {/* Logo */}
          {logo && (
            <Box flexShrink={0}>
              {logo}
            </Box>
          )}

          {/* Title */}
          {title && (
            <Heading
              size="md"
              color={titleColor}
              display={{ base: 'none', sm: 'block' }}
              noOfLines={1}
            >
              {title}
            </Heading>
          )}
        </HStack>

        {/* Spacer to push right content */}
        <Spacer />

        {/* Right Section: Org Switcher + Actions + User Menu */}
        <HStack spacing={{ base: 2, md: 4 }}>
          {/* Organization Switcher (for multi-tenant) */}
          {orgSwitcher && (
            <Box display={{ base: 'none', md: 'block' }}>
              {orgSwitcher}
            </Box>
          )}

          {/* Action Buttons (theme toggle, notifications, etc.) */}
          {actions && (
            <HStack spacing={2}>
              {actions}
            </HStack>
          )}

          {/* User Menu */}
          {userMenu && (
            <Box>
              {userMenu}
            </Box>
          )}
        </HStack>
      </Flex>
    </Box>
  );
}

// ============================================================================
// Exports
// ============================================================================

export default Header;
